import { Controller } from "@hotwired/stimulus"

/**
 * Saved-report schedule form (CONEK-8901), in the list's drawer and on the
 * full New/Edit pages.
 *
 * Two jobs:
 *   - "Day of week" shows only while Frequency is Weekly. The model ignores
 *     day_of_week for the other frequencies, so the field still submits when
 *     hidden; it just stops asking a question that has no effect.
 *   - "Reports to send": every slot the schedule allows is already in the
 *     markup, and the server renders the filled ones plus one empty one
 *     visible. Add reveals the next hidden slot; × clears a slot and hides it.
 *     The controller never adds or removes DOM nodes, so a hidden slot simply
 *     submits an empty report, which SavedReportSchedulesController's
 *     blank-row filter drops. Same contract as iot--compare-toggle.
 *
 * Removing a slot in the MIDDLE shifts every later slot's values up, so the
 * visible slots stay contiguous and the next Add appears at the bottom, where
 * the operator is looking — not in a hole above it.
 *
 * Each slot's selects are Ui::SelectComponent, i.e. Tom Select over the
 * native <select>. Tom Select keeps its own state, so values go through the
 * instance it hangs on the element (`select.tomselect`); writing `.value`
 * alone would leave the visible control showing the old choice.
 *
 * `hidden` is the native attribute: a hidden slot must drop out of layout.
 *
 * CONEK-9314 — each slot also shows the date window the next email covers
 * for its range. The server hands over every range's window for that email
 * (`windows`, range => text), so a range change only swaps text; the shift
 * done by × is silent, so it re-syncs every slot afterwards. The windows (and
 * the "next email" date) belong to the cadence the form opened with, so once
 * Frequency, Day, Time or Time zone changes they are hidden rather than left
 * showing dates that no longer hold; saving brings them back.
 *
 * Markup contract:
 *   <form data-controller="iot--schedule-form"
 *         data-iot--schedule-form-default-range-value="last_7_days"
 *         data-iot--schedule-form-windows-value='{"last_7_days": "…"}'>
 *     <select data-iot--schedule-form-target="frequency"
 *             data-action="change->iot--schedule-form#syncDay">…</select>
 *     <div data-iot--schedule-form-target="dayField">…</div>
 *     <div data-iot--schedule-form-target="row" hidden?>
 *       <select data-iot--schedule-form-target="report">…</select>
 *       <select data-iot--schedule-form-target="range"
 *               data-action="change->iot--schedule-form#syncWindows">…</select>
 *       (the report select carries the same action: a slot shows its window
 *       only once it has a report)
 *       <p data-iot--schedule-form-target="window">…</p>
 *       <button data-action="iot--schedule-form#remove">×</button>
 *     </div> × MAX_ITEMS
 *     <button data-iot--schedule-form-target="addButton"
 *             data-action="iot--schedule-form#add">Add report</button>
 *   </form>
 */
export default class extends Controller {
  static targets = [
    "frequency",
    "dayField",
    "row",
    "report",
    "range",
    "window",
    "nextSend",
    "addButton",
  ]
  static values = { defaultRange: String, windows: Object }

  connect() {
    this.syncDay()
    this.syncAddButton()
    this.syncWindows()
  }

  syncDay() {
    if (!this.hasFrequencyTarget || !this.hasDayFieldTarget) return

    this.dayFieldTarget.hidden = this.frequencyTarget.value !== "weekly"
  }

  add() {
    const next = this.rowTargets.find((row) => row.hidden)
    if (!next) return

    next.hidden = false
    this.syncAddButton()
  }

  remove(event) {
    const row = event.target.closest("[data-iot--schedule-form-target~='row']")
    const index = this.rowTargets.indexOf(row)
    if (index < 0) return

    const visible = this.visibleRows()
    const lastIndex = this.rowTargets.indexOf(visible[visible.length - 1])
    for (let i = index; i < lastIndex; i++) {
      this.copyRow(this.rowTargets[i + 1], this.rowTargets[i])
    }

    const last = this.rowTargets[lastIndex]
    this.writeRow(last, "", this.defaultRangeValue)
    // Never hide the only slot: an empty form still offers one row to fill.
    if (visible.length > 1) last.hidden = true
    this.syncAddButton()
    this.syncWindows()
  }

  cadenceChanged() {
    this.cadenceStale = true
    this.nextSendTargets.forEach((el) => (el.hidden = true))
    this.syncWindows()
  }

  syncWindows() {
    this.rowTargets.forEach((row) => {
      const windowText = this.selectIn(row, "window")
      if (!windowText) return

      // An empty slot sends nothing, so it promises no dates either.
      const report = this.selectIn(row, "report")?.value
      windowText.hidden = Boolean(this.cadenceStale) || !report
      const range = this.selectIn(row, "range")?.value || this.defaultRangeValue
      windowText.textContent = this.windowsValue[range] ?? ""
    })
  }

  syncAddButton() {
    if (!this.hasAddButtonTarget) return

    this.addButtonTarget.hidden =
      this.visibleRows().length >= this.rowTargets.length
  }

  visibleRows() {
    return this.rowTargets.filter((row) => !row.hidden)
  }

  copyRow(from, to) {
    this.writeRow(
      to,
      this.selectIn(from, "report")?.value ?? "",
      this.selectIn(from, "range")?.value ?? this.defaultRangeValue,
    )
  }

  writeRow(row, report, range) {
    this.setSelect(this.selectIn(row, "report"), report)
    this.setSelect(this.selectIn(row, "range"), range)
  }

  selectIn(row, target) {
    return row.querySelector(`[data-iot--schedule-form-target~='${target}']`)
  }

  setSelect(select, value) {
    if (!select) return

    if (select.tomselect) {
      // `true` = silent: no change event per shifted slot.
      if (value === "") select.tomselect.clear(true)
      else select.tomselect.setValue(value, true)
    } else {
      select.value = value
    }
  }
}
